/**
 * Roster tab — pending-pinned enrollment lifecycle controls.
 *
 * Per design-system/ui_kits/mvp/admin.html:520-614. Pending rows pinned
 * to the top with a warn left-border; admin clicks Approve (green
 * outline) or Reject (red icon-only) inline. A 5 s undo toast surfaces
 * after each action so a mistaken approve can be reverted before the
 * backend ever sees it.
 *
 * Lifecycle wiring:
 *   pending  → armed     (Approve)
 *   pending  → rejected  (Reject, terminal in MVP)
 *   armed    → disarmed  (Disarm)
 *   disarmed → armed     (Rearm)
 *
 * Approve + Rearm trigger geofence_set re-emit to the phone; the Roster
 * tab doesn't need to know that. The 409 races (two admins acting
 * simultaneously) surface as caller-swallowed errors because the
 * optimistic UI updates immediately; the backend's "allowed_from" guard
 * is the source of truth at commit time.
 *
 * Out of scope (intentional carve-outs):
 *   - Import CSV / QR sheet PDF / Add vehicle buttons (wireframe shows
 *     them; drivers self-claim via /api/fleet/join — no bulk-admin path)
 *   - Car # column (we have device_id and driver_name, not race numbers
 *     — race-number ingestion is post-MVP)
 *   - Online / Offline filter chips (FleetDevice live-status join is
 *     deferred; the enrollment state model is sufficient for MVP)
 *   - more_vert kebab menu (we surface state-specific actions inline)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Search, Users, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import UndoToast, { PendingAction } from '@/components/UndoToast';
import {
  EnrollmentRow,
  adoptEnrollment,
  EnrollmentState,
  FleetApiError,
  approveEnrollment,
  disarmEnrollment,
  listEnrollments,
  patchEnrollmentLabels,
  rearmEnrollment,
  rejectEnrollment,
} from '@/services/fleet';
import { useCurrentEvent } from '@/state/currentEvent';
import { cn, displayVehicle } from '@/lib/utils';

const POLL_INTERVAL_MS = 5_000;

type FilterKind = 'all' | EnrollmentState;

const FILTERS: { key: FilterKind; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'armed', label: 'Armed' },
  { key: 'disarmed', label: 'Disarmed' },
  { key: 'rejected', label: 'Rejected' },
];

export const RosterTab: React.FC = () => {
  // Events list + selection live in the global CurrentEventProvider.
  const { currentEventId, loading: loadingEvents } = useCurrentEvent();
  const selectedEventId = currentEventId;
  const [rows, setRows] = useState<EnrollmentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKind>('all');
  const [pending, setPending] = useState<PendingAction | null>(null);

  // Optimistic local overrides — set before commit, cleared on resolve
  // or rollback. Keyed by enrollment id; value = the state we expect
  // post-commit. listEnrollments refreshes may overwrite if the
  // backend disagrees (e.g. our action raced and lost).
  const optimisticRef = useRef<Map<number, EnrollmentState>>(new Map());

  const refreshRows = useCallback(async () => {
    if (selectedEventId == null) return;
    try {
      const data = await listEnrollments(selectedEventId);
      // Reconcile optimistic overlay: for each row in `optimisticRef`,
      // if backend now reflects (or surpasses) our expected state,
      // drop the override. Otherwise keep showing the optimistic state
      // until the commit lands or rolls back.
      const overlay = optimisticRef.current;
      const merged = data.map((r) => {
        const expected = overlay.get(r.id);
        if (expected != null && r.state === expected) {
          overlay.delete(r.id);
        }
        return overlay.has(r.id) ? { ...r, state: overlay.get(r.id)! } : r;
      });
      setRows(merged);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load roster');
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (selectedEventId == null) return;
    void refreshRows();
    const id = window.setInterval(refreshRows, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [selectedEventId, refreshRows]);

  const counts = useMemo(() => {
    const c: Record<FilterKind, number> = {
      all: rows.length,
      pending: 0,
      armed: 0,
      rejected: 0,
      disarmed: 0,
    };
    for (const r of rows) c[r.state] = (c[r.state] || 0) + 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matchesText = (r: EnrollmentRow) =>
      !term ||
      (r.driver_name || '').toLowerCase().includes(term) ||
      (r.vehicle || '').toLowerCase().includes(term) ||
      (r.join_message || '').toLowerCase().includes(term) ||
      r.device_id.toLowerCase().includes(term);
    const matchesFilter = (r: EnrollmentRow) =>
      filter === 'all' ? true : r.state === filter;
    const subset = rows.filter((r) => matchesText(r) && matchesFilter(r));
    // Pending pinned top; within each group, newest first.
    return [...subset].sort((a, b) => {
      const aPending = a.state === 'pending' ? 0 : 1;
      const bPending = b.state === 'pending' ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      return Date.parse(b.created_at) - Date.parse(a.created_at);
    });
  }, [rows, search, filter]);

  /**
   * Fire a state-transition API call immediately, with optimistic UI.
   *
   * Previously this scheduled the commit behind a 5 s undo window —
   * silently dropping actions when the operator refreshed/navigated
   * within 5 s. Now the API fires at click time and the toast is
   * display-only. Recovery from an accidental reject needs a backend
   * reverse-transition endpoint (rejected → pending), not a
   * client-side timer.
   */
  const scheduleTransition = (
    row: EnrollmentRow,
    nextState: EnrollmentState,
    apiCall: () => Promise<unknown>,
    label: string,
  ) => {
    const prev = row.state;
    optimisticRef.current.set(row.id, nextState);
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, state: nextState } : r)));
    setPending({
      key: `${row.id}-${nextState}-${Date.now()}`,
      message: `${label} ${row.driver_name || row.device_id}`,
    });

    void apiCall()
      .catch((err) => {
        // 409 = state moved underneath us (another admin acted first);
        // the backend's allowed_from guard rejected our transition.
        // Let the next poll reconcile; no need to rollback the
        // optimistic state — refreshRows will overwrite.
        if (err instanceof FleetApiError && err.status === 409) return;
        // eslint-disable-next-line no-console
        console.warn('[roster] action failed', err);
        // Roll back the optimistic state on unexpected failure so the
        // operator doesn't see a phantom transition.
        setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, state: prev } : r)));
      })
      .finally(() => {
        optimisticRef.current.delete(row.id);
        void refreshRows();
      });
  };

  const handleApprove = (row: EnrollmentRow) => {
    if (selectedEventId == null) return;
    scheduleTransition(
      row,
      'armed',
      () => approveEnrollment(selectedEventId, row.id),
      'Approved',
    );
  };
  const handleReject = (row: EnrollmentRow) => {
    if (selectedEventId == null) return;
    scheduleTransition(
      row,
      'rejected',
      () => rejectEnrollment(selectedEventId, row.id),
      'Rejected',
    );
  };
  const handleDisarm = (row: EnrollmentRow) => {
    if (selectedEventId == null) return;
    scheduleTransition(
      row,
      'disarmed',
      () => disarmEnrollment(selectedEventId, row.id),
      'Disarmed',
    );
  };
  const handleRearm = (row: EnrollmentRow) => {
    if (selectedEventId == null) return;
    scheduleTransition(
      row,
      'armed',
      () => rearmEnrollment(selectedEventId, row.id),
      'Re-armed',
    );
  };
  // Car # inline edit. Optimistic (label-only, no state machine);
  // a failed PATCH reverts via the reload poll within 5 s.
  /** 'same phone as…' — the (usually pending) row adopts an older
   *  enrollment's labels + arm state; the old row is retired. */
  const handleAdopt = (row: EnrollmentRow, fromId: number) => {
    if (selectedEventId == null) return;
    setPending({
      key: `${row.id}-adopt-${fromId}`,
      message: `Adopting identity for ${row.driver_name || row.device_id}…`,
    });
    void adoptEnrollment(selectedEventId, row.id, fromId)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[roster] adopt failed', err);
      })
      .finally(() => void refreshRows());
  };

  const handleCarNumber = (row: EnrollmentRow, value: string) => {
    if (selectedEventId == null) return;
    const next = value.trim() || null;
    if (next === (row.car_number ?? null)) return;
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, car_number: next } : r)));
    void patchEnrollmentLabels(selectedEventId, row.id, { car_number: next }).catch((err) => {
      setError(err instanceof Error ? err.message : 'car number save failed');
    });
  };

  if (loadingEvents) {
    return (
      <div className="mx-auto mt-12 flex max-w-5xl items-center justify-center px-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto mb-8 mt-4 max-w-6xl px-2 sm:px-6">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-accent" />
          <div>
            <h1 className="text-2xl font-semibold leading-tight">Roster</h1>
            <p className="text-sm text-fg-secondary">
              Pending sign-ups pinned to the top. Drivers self-claim by scanning the event QR
              from the <span className="font-semibold">Event</span> tab.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
          {error}
        </div>
      )}

      {selectedEventId == null ? (
        <Card className="p-6">
          <CardContent className="p-0">
            <p className="text-sm text-fg-secondary">
              Create an event on the <span className="font-semibold">Event</span> tab to start
              collecting roster sign-ups.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-fg-secondary" />
              <Input
                placeholder="Search driver, vehicle, device id…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={cn(
                  'flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  filter === f.key
                    ? f.key === 'pending'
                      ? 'border-status-warning bg-status-warning text-bg-base'
                      : 'border-accent bg-accent text-bg-base'
                    : 'border-border bg-bg-surface text-fg-secondary hover:bg-bg-elevated',
                )}
              >
                {f.label}
                <span
                  className={cn(
                    'ml-1 font-mono text-[10px]',
                    filter === f.key ? 'text-bg-base/70' : 'text-fg-secondary',
                  )}
                >
                  {counts[f.key]}
                </span>
              </button>
            ))}
          </div>

          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="grid grid-cols-[64px_1.4fr_1.6fr_120px_1fr_240px] items-center gap-2 border-b border-border bg-bg-elevated px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-fg-secondary">
                <span>Car #</span>
                <span>Driver</span>
                <span>Vehicle / device</span>
                <span>Status</span>
                <span>Submitted</span>
                <span className="text-right">Actions</span>
              </div>
              {filtered.length === 0 ? (
                <div className="px-3 py-8 text-center text-sm text-fg-secondary">
                  No matching enrollments.
                </div>
              ) : (
                filtered.map((row) => (
                  <RosterRowItem
                    key={row.id}
                    row={row}
                    onApprove={() => handleApprove(row)}
                    adoptCandidates={rows.filter(
                      (r) =>
                        r.id !== row.id &&
                        r.device_id !== row.device_id &&
                        (r.state === 'armed' || r.state === 'disarmed'),
                    )}
                    onAdopt={(fromId) => handleAdopt(row, fromId)}
                    onReject={() => handleReject(row)}
                    onDisarm={() => handleDisarm(row)}
                    onRearm={() => handleRearm(row)}
                    onCarNumber={(value) => handleCarNumber(row, value)}
                  />
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}

      <UndoToast pending={pending} onResolved={() => setPending(null)} />
    </div>
  );
};

export default RosterTab;

// ────────────────────────────────────────────────────────────────────────
// Row + chips
// ────────────────────────────────────────────────────────────────────────

const RosterRowItem: React.FC<{
  row: EnrollmentRow;
  onApprove: () => void;
  onReject: () => void;
  onDisarm: () => void;
  onRearm: () => void;
  onCarNumber: (value: string) => void;
  /** Enrollments this row could adopt ("same phone as…"). */
  adoptCandidates?: EnrollmentRow[];
  onAdopt?: (fromEnrollmentId: number) => void;
}> = ({ row, onApprove, onReject, onDisarm, onRearm, onCarNumber, adoptCandidates = [], onAdopt }) => {
  const isPending = row.state === 'pending';
  return (
    <div
      className={cn(
        'grid grid-cols-[64px_1.4fr_1.6fr_120px_1fr_240px] items-center gap-2 border-b border-border px-3 py-2 text-sm',
        isPending && 'border-l-4 border-l-status-warning bg-status-warning/5',
      )}
    >
      {/* Race number (one device = one car). Uncontrolled: the
          list poll refreshes rows every 5 s and a controlled input
          would fight mid-typing; keyed on the saved value so a
          server-side change re-seeds it. Saves on blur/Enter. */}
      <Input
        key={row.car_number ?? ''}
        defaultValue={row.car_number ?? ''}
        maxLength={16}
        placeholder="#"
        className="h-7 px-1.5 text-center font-mono text-xs"
        aria-label={`Car number for ${row.driver_name || row.device_id}`}
        onBlur={(e) => onCarNumber(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      <div>
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{row.driver_name || '—'}</span>
          {/* Join-form filming role; legacy clients send none. */}
          {row.role && (
            <span className="rounded-full border border-border bg-bg-elevated px-1.5 py-px text-[9px] font-medium uppercase tracking-wider text-fg-secondary">
              {row.role}
            </span>
          )}
        </div>
        <div className="text-[11px] text-fg-secondary">{row.device_id}</div>
      </div>
      <div>
        {/* "." = operator's intentional-blank convention (displayVehicle). */}
        <div>{displayVehicle(row.vehicle) || '—'}</div>
        {/* The driver's pitch to the organizer (join form). */}
        {row.join_message && (
          <div
            className="max-w-[320px] truncate text-[11px] italic text-fg-secondary"
            title={row.join_message}
          >
            “{row.join_message}”
          </div>
        )}
        {row.rejected_reason && (
          <div className="text-[11px] text-status-error">{row.rejected_reason}</div>
        )}
      </div>
      <div>
        <StateChip state={row.state} />
      </div>
      <div className="text-xs text-fg-secondary">
        {formatRelativeTime(row.created_at)}
      </div>
      <div className="flex justify-end gap-1">
        {row.state === 'pending' && (
          <>
            <Button
              type="button"
              size="sm"
              variant="default"
              onClick={onApprove}
              className="h-7 gap-1 border border-status-online/60 bg-status-online/15 text-status-online hover:bg-status-online/25"
            >
              <Check className="size-3.5" />
              Approve
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onReject}
              aria-label="Reject"
              title="Reject"
              className="h-7 w-7 p-0 text-status-error hover:bg-status-error/15"
            >
              <X className="size-4" />
            </Button>
            {/* 'same phone as…' — an accidental re-enrollment adopts
                an earlier row's identity (labels, car #, arm state);
                the old row is retired with an audit reason. */}
            {adoptCandidates.length > 0 && onAdopt && (
              <select
                value=""
                title="This phone re-enrolled? Adopt an earlier enrollment's identity — labels, car number and arm state carry over; the old row is retired."
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v) onAdopt(v);
                  e.currentTarget.value = '';
                }}
                className="h-7 max-w-[150px] cursor-pointer rounded border border-border bg-bg-input px-1 text-[11px] text-fg-secondary hover:border-accent"
              >
                <option value="">same phone as…</option>
                {adoptCandidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.car_number ? `#${c.car_number} ` : ''}
                    {c.driver_name || c.device_id}
                  </option>
                ))}
              </select>
            )}
          </>
        )}
        {row.state === 'armed' && (
          <>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onDisarm}
              className="h-7 text-fg-secondary hover:text-fg"
            >
              Disarm
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onReject}
              aria-label="Kick (reject after approve)"
              title="Kick (reject after approve)"
              className="h-7 w-7 p-0 text-status-error hover:bg-status-error/15 hover:text-status-error"
            >
              <X className="size-4" />
            </Button>
          </>
        )}
        {row.state === 'disarmed' && (
          <>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onRearm}
              className="h-7 text-accent hover:text-accent"
            >
              Re-arm
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onReject}
              aria-label="Reject"
              title="Reject"
              className="h-7 w-7 p-0 text-status-error hover:bg-status-error/15 hover:text-status-error"
            >
              <X className="size-4" />
            </Button>
          </>
        )}
        {row.state === 'rejected' && (
          <span className="text-xs italic text-fg-secondary">terminal</span>
        )}
      </div>
    </div>
  );
};

const StateChip: React.FC<{ state: EnrollmentState }> = ({ state }) => {
  const style = STATE_CHIP_STYLE[state];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
        style,
      )}
    >
      {state}
    </span>
  );
};

const STATE_CHIP_STYLE: Record<EnrollmentState, string> = {
  pending: 'bg-status-warning/15 text-status-warning border border-status-warning/30',
  armed: 'bg-status-online/15 text-status-online border border-status-online/30',
  disarmed: 'bg-fg-secondary/15 text-fg-secondary border border-fg-secondary/30',
  rejected: 'bg-status-error/15 text-status-error border border-status-error/30',
};

function formatRelativeTime(iso: string): string {
  const dt = Date.parse(iso);
  if (!Number.isFinite(dt)) return '—';
  const deltaMs = Date.now() - dt;
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
